import { toastConfig } from 'config';
import { toast } from 'react-toastify';
import { decodeQueryParamData } from 'utils/utils';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { openDrawer } from 'store/features/menu/menuSlice';
import { useRef, useState, useEffect, useMemo } from 'react';
import { useNodesState, useEdgesState } from '@xyflow/react';
import {
	useGetWorkFlowDesignByIdMutation,
	useWorkFlowSolutionUpgradeMutation,
	useWorkFlowDesignBulkActiveMutation,
	useWorkFlowDesignBulkDeactivateMutation,
	useWorkFlowConditionMasterUpgradeMutation,
} from 'store/features/process/processApi';

const useViewWorkFlowHook = () => {
	const params = useParams();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const flowInstance = useRef(null);
	const reactFlowWrapper = useRef(null);

	const [nodes, setNodes] = useNodesState([]);
	const [edges, setEdges] = useEdgesState([]);

	const [value, setValue] = useState(null);
	const [modalData, setModalData] = useState({});
	const [showModal, setShowModal] = useState(false);
	const [submitData, setSubmitData] = useState(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedNode, setSelectedNode] = useState(null);
	const [selectedEdge, setSelectedEdge] = useState(null);
	const [activationStatus, setActivationStatus] = useState('');
	const [isEdgeModalOpen, setIsEdgeModalOpen] = useState(false);

	const { profileData } = useSelector((state) => state?.profile);

	const [getWorkFlowDesignById, { data: workFlowDesignData }] = useGetWorkFlowDesignByIdMutation();
	const [workflowDesignActiveHandler, { data: workflowActiveHandleData, isError: isActivateError }] = useWorkFlowDesignBulkActiveMutation();
	const [workflowSolutionUpgradeHandler, { data: workflowSolutionUpgradeHandleData, isUpgradeError }] = useWorkFlowSolutionUpgradeMutation();
	const [workflowDesignDeactivateHandler, { data: workflowDeactivateHandleData, isError: isDeactivateError }] =
		useWorkFlowDesignBulkDeactivateMutation();
	const [workflowConditionUpgradeHandler, { data: workflowConditionUpgradeHandleData, isError: isConditionUpgradeError }] =
		useWorkFlowConditionMasterUpgradeMutation();

	const state = useMemo(() => {
		return decodeQueryParamData(params?.id);
	}, []);

	const dataDecode = useMemo(() => {
		if (workFlowDesignData?.result) {
			setActivationStatus(workFlowDesignData?.result?.activationStatus);

			return decodeQueryParamData(workFlowDesignData?.result?.designBase64);
		} else {
			return null;
		}
	}, [workFlowDesignData]);

	const handleViewSolution = () => {
		setIsModalOpen(true);

		const solution = nodes?.find((item) => item?.type === 'solution') ?? null;

		setSelectedNode(solution);
	};

	const handleEditFlow = () => {
		navigate(`/comptroller/work-flow/edit/${params?.id}`);
	};

	const handleWorkflowAction = ({ action }) => {
		if (action === 'Deactivate') {
			setValue('deactivated');

			setModalData({
				head: 'Workflow Deactivated',
				label: (
					<p>
						Do you want to <b style={{ fontSize: '1rem' }}>deactivate</b>?
					</p>
				),
			});

			setSubmitData([
				{
					id: state?.id,
					companyCode: profileData?.creatorOrganizationId,
					activationStatus: 'Active',
				},
			]);
		} else if (action === 'Active') {
			setValue('activated');

			setModalData({
				head: 'Workflow active',
				label: (
					<p>
						Do you want to <b style={{ fontSize: '1rem' }}>active</b>?
					</p>
				),
			});

			setSubmitData([
				{
					id: state?.id,
					companyCode: profileData?.creatorOrganizationId,
					activationStatus: 'Inactive',
				},
			]);
		} else if (action === 'Upgrade') {
			setValue('upgrade');

			setModalData({
				head: 'Workflow solution upgrade',
				label: (
					<p>
						Do you want to <b style={{ fontSize: '1rem' }}>solution upgrade</b>?
					</p>
				),
			});

			setSubmitData({
				...workFlowDesignData?.result,
				solution: {
					modifiedBy: profileData?.userId,
					id: workFlowDesignData?.result?.solution?.id,
					code: workFlowDesignData?.result?.solution?.code,
					name: workFlowDesignData?.result?.solution?.name,
				},
			});
		} else if (action === 'new_version') {
			setValue('new_version');

			setModalData({
				head: 'Workflow new condition version',
				label: (
					<p>
						Do you want to <b style={{ fontSize: '1rem' }}>new condition version</b>?
					</p>
				),
			});

			setSubmitData({
				...workFlowDesignData?.result,
				conditionMaster: {
					id: '6921c02dc16f2e586b8c43ee',
					companyCode: profileData?.creatorOrganizationId,
				},
			});
		}

		setShowModal(true);
	};

	const handleActionSubmit = () => {
		if (submitData) {
			if (value === 'activated') {
				workflowDesignActiveHandler({ data: submitData });

				setValue(null);
			} else if (value === 'deactivated') {
				workflowDesignDeactivateHandler({ data: submitData });

				setValue(null);
			} else if (value === 'upgrade') {
				workflowSolutionUpgradeHandler(submitData);

				setValue(null);
			} else if (value === 'new_version') {
				workflowConditionUpgradeHandler(submitData);

				setValue(null);
			}
		}
	};

	useEffect(() => {
		dispatch(openDrawer({ drawerOpen: false }));
	}, []);

	useEffect(() => {
		if (state?.id) {
			getWorkFlowDesignById({ id: state?.id });
		}
	}, [state]);

	useEffect(() => {
		if (dataDecode) {
			const nodeList = dataDecode?.nodes?.map((item) => ({
				...item,
				data: {
					...item?.data,

					processCode: workFlowDesignData?.result?.solution?.code,
					processVersion: workFlowDesignData?.result?.solution?.version,
				},
			}));

			setNodes(nodeList);
			setEdges(dataDecode?.edges);
		}
	}, [dataDecode, workFlowDesignData]);

	useEffect(() => {
		if (workflowActiveHandleData?.status === 'SUCCESS') {
			let message = submitData?.length > 1 ? `Success! ${submitData?.length} rows ${value}` : `Success! ${submitData?.length} row ${value}`;

			toast.success(message, toastConfig);
			navigate('/comptroller/work-flow');
		} else if (isActivateError) {
			toast.error('Workflow Status Updation Error!', toastConfig);
		}
	}, [workflowActiveHandleData, isActivateError]);

	useEffect(() => {
		if (workflowDeactivateHandleData?.status === 'SUCCESS') {
			let message = submitData?.length > 1 ? `Success! ${submitData?.length} rows ${value}` : `Success! ${submitData?.length} row ${value}`;

			toast.success(message, toastConfig);
			navigate('/comptroller/work-flow');
		} else if (isDeactivateError) {
			toast.error('Workflow Status Updation Error!', toastConfig);
		}
	}, [workflowDeactivateHandleData, isDeactivateError]);

	useEffect(() => {
		if (workflowSolutionUpgradeHandleData?.status === 'SUCCESS') {
			toast.success(`Success! Solution upgrade`, toastConfig);
			navigate('/comptroller/work-flow');
		} else if (isUpgradeError) {
			toast.error('Workflow Solution Upgrade Error!', toastConfig);
		}
	}, [workflowSolutionUpgradeHandleData, isUpgradeError]);

	useEffect(() => {
		if (workflowConditionUpgradeHandleData?.status === 'SUCCESS') {
			toast.success(`Success! Condition upgrade`, toastConfig);
			navigate('/comptroller/work-flow');
		} else if (isConditionUpgradeError) {
			toast.error('Workflow Condition Upgrade Error!', toastConfig);
		}
	}, [workflowConditionUpgradeHandleData, isConditionUpgradeError]);

	return {
		nodes,
		edges,
		setNodes,
		setEdges,
		showModal,
		modalData,
		isModalOpen,
		flowInstance,
		setShowModal,
		selectedNode,
		selectedEdge,
		handleEditFlow,
		setIsModalOpen,
		setSelectedNode,
		isEdgeModalOpen,
		setSelectedEdge,
		activationStatus,
		reactFlowWrapper,
		setIsEdgeModalOpen,
		handleActionSubmit,
		handleViewSolution,
		handleWorkflowAction,
	};
};

export default useViewWorkFlowHook;
